import React, { useEffect, useRef, useState } from 'react';
import { Box, DialogContent, Backdrop, Button, TextField, Typography, InputAdornment, IconButton, Link, Paper, Container, Grow, Dialog, DialogTitle, DialogActions } from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CustomModal from 'Common/CustomModal';
import useKeycloakAdapter from 'Hooks/useKeycloakAdapter';

import API from '../../api';
import styles from "./index.module.scss";
import { InputField } from 'JsUtils/FormikControls';

const LoginFormUI = ({ handleClose, setUserLoginData }) => {
    const { roleType } = useParams();
    const [showPassword, setShowPassword] = useState(false);
    const [backdrop, setBackdrop] = useState(false);
    const [userType, setUserType] = useState("");
    const [mfaRequired, setMfaRequired] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const usernameRef = useRef('');
    const passwordRef = useRef('');
    const userTypeRef = useRef('');

    // Setup Modal State
    const [showKeycloakSetup, setShowKeycloakSetup] = useState(false);
    const [setupCheckLoading, setSetupCheckLoading] = useState(false);
    const [setupPopup, setSetupPopup] = useState(null);

    const keycloakAdapter = useKeycloakAdapter();

    // Forgot Password Modal State
    const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
    const [forgotPasswordError, setForgotPasswordError] = useState('');

    useEffect(() => {
        setUserType(roleType);
        userTypeRef.current = roleType;
    }, [roleType]);

    const openKeycloakSetup = async () => {
        try {
            const realm = userType === 'advisor' ? 'TASAdvisor' : 'TASClient';
            const clientIdForKeycloak = userType === 'client' ? `tasclientauth` : "tasadvisorauth";

            // Skip initialization - directly open account management
            const popup = keycloakAdapter.openAccountManagement(realm, clientIdForKeycloak);
            
            if (popup) {
                setSetupPopup(popup);
                toast.info('📱 Account setup window opened. Please complete your setup and click "Setup Complete" when done.');
            } else {
                toast.error('Failed to open setup window. Please check if popups are blocked.');
            }
        } catch (err) {
            console.error('Failed to open Keycloak setup:', err);
            toast.error('Failed to open setup window. Please try again.');
        }
    };

    // Monitor popup window: when it closes, automatically attempt verification
    useEffect(() => {
        let timer;
        if (setupPopup) {
            timer = setInterval(() => {
                try {
                    if (setupPopup.closed) {
                        clearInterval(timer);
                        setSetupPopup(null);
                        toast.info('Setup window closed. Verifying setup...');
                        // Auto-check setup completion when popup closes
                        checkSetupCompletion(true, usernameRef.current, passwordRef.current, userTypeRef.current);
                    }
                } catch (e) {
                    // Accessing popup properties can throw if cross-origin; ignore and continue
                }
            }, 1000);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [setupPopup]);


    // Cleanup popup if component unmounts
    useEffect(() => {
        return () => {
            try {
                if (setupPopup && !setupPopup.closed) {
                    setupPopup.close();
                }
            } catch (e) {
                // ignore
            }
        };
    }, [setupPopup]);

    // Handle Forgot Password Modal
    const handleOpenForgotPasswordModal = () => {
        setForgotPasswordModalOpen(true);
        setForgotPasswordEmail('');
        setForgotPasswordSuccess(false);
        setForgotPasswordError('');
    };

    const handleCloseForgotPasswordModal = () => {
        setForgotPasswordModalOpen(false);
        setForgotPasswordEmail('');
        setForgotPasswordSuccess(false);
        setForgotPasswordError('');
    };

    const handleForgotPasswordSubmit = async () => {
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!forgotPasswordEmail || !emailRegex.test(forgotPasswordEmail)) {
            setForgotPasswordError('Please enter a valid email address');
            return;
        }

        setForgotPasswordLoading(true);
        setForgotPasswordError('');

        try {
            const response = await API.auth.requestPasswordReset({
                email: forgotPasswordEmail,
                userType: userType
            });

            if (response.data.success) {
                setForgotPasswordSuccess(true);
                // Auto-close modal after 2 seconds
                setTimeout(() => {
                    handleCloseForgotPasswordModal();
                }, 2000);
            } else {
                setForgotPasswordError(response.data.message || 'Failed to send reset link');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to send reset link';
            setForgotPasswordError(errorMessage);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    const checkSetupCompletion = async (showLoadingState = true, _username = '', _password = '', _userType = '') => {
        if (!_username || !_password) {
            toast.error('Session expired. Please try logging in again.');
            setShowKeycloakSetup(false);
            return;
        }

        if (showLoadingState) {
            setSetupCheckLoading(true);
        }

        try {
            const loginData = {
                username: _username,
                password: _password,
                userType: _userType
            };

            console.log('🔄 Verifying setup completion...');
            const response = await API.auth.loginForPayment(loginData);

            if (response.status === 200 || response.status === 201) {
                setShowKeycloakSetup(false);
                toast.success('🎉 Perfect! Login successful!');
                handleSuccessfulLogin(response.data);
            } else if (response.status === 203 && response.data.mfaRequired) {
                setShowKeycloakSetup(false);
                setMfaRequired(true);
                toast.success('✅ Great! Now enter your authentication code.');
            }
        } catch (err) {
            console.log('Setup check result:', err?.response?.status);

            // Provide specific feedback based on error
            if (err?.response?.status === 428 ||
                (err?.response?.status === 400 &&
                    err?.response?.data?.error_description?.includes('not fully set up'))) {
                toast.warning('Setup still in progress. Please complete all steps above and try again.');
            } else if (err?.response?.status === 401) {
                toast.warning('Please complete the authenticator setup and try again.');
            } else {
                toast.error('Unable to verify setup. Please try logging in again after completing setup.');
            }
        } finally {
            if (showLoadingState) {
                setSetupCheckLoading(false);
            }
        }
    };

    const initialValues = { username: '', password: '' };

    const validationSchema = Yup.object({
        username: Yup.string().required('Email is required'),
        password: Yup.string().required('Password is required'),
    });

    const HandleSubmit = async (values) => {
        setUsername(values.username);
        setPassword(values.password);
        usernameRef.current = values.username;
        passwordRef.current = values.password;

        const loginData = {
            username: values.username,
            password: values.password,
            userType: userType
        };

        try {
            const response = await API.auth.loginForPayment(loginData);

            if (response.status === 203 && response.data.mfaRequired) {
                setMfaRequired(true);
                setBackdrop(false);
                toast.info('Please enter your authentication code');
            } else if (response.status === 200 || response.status === 201) {
                handleSuccessfulLogin(response.data);
            } else {
                if (response.data.message) {
                    toast.error(response.data.message);
                } else {
                    toast.error('Invalid Credentials');
                }
                setBackdrop(false);
            }
        } catch (err) {
            setBackdrop(false);

            // SHOW POPUP-BASED KEYCLOAK SETUP FOR SETUP REQUIRED
            if (err?.response?.status === 428 && err?.response?.data?.setupRequired) {
                setShowKeycloakSetup(true);
                toast.info('Please complete your account setup in the popup window');
            }
            else if (err?.response?.status === 400 &&
                err?.response?.data?.error === 'invalid_grant' &&
                err?.response?.data?.error_description?.includes('Account is not fully set up')) {

                setShowKeycloakSetup(true);
                toast.info('Please complete your account setup in the popup window');
            } else {
                console.log('Login error:', err?.response?.data?.message);
                // toast.error(err?.response?.data?.message || 'Invalid Credentials');
            }
        }
    };

    const mfaInitialValues = { otpCode: '' };

    const mfaValidationSchema = Yup.object({
        otpCode: Yup.string().required('Authentication code is required')
            .min(6, 'Code must be at least 6 characters')
            .max(10, 'Code cannot be more than 10 characters')
    });

    const HandleMfaSubmit = async (values) => {
        // Create the complete MFA data object
        const mfaData = {
            username: username,
            password: password,
            userType: userType,
            otpCode: values.otpCode
        };

        try {
            const response = await API.auth.loginForPayment(mfaData);

            if (response.status === 200 || response.status === 201) {
                handleSuccessfulLogin(response.data);
            } else {
                if (response.data.message) {
                    toast.error(response.data.message);
                } else {
                    toast.error('Invalid Authentication Code');
                }
                setBackdrop(false);
            }
        } catch (err) {
            setBackdrop(false);
            toast.error(err?.response?.data?.message || 'Invalid Authentication Code');
        }
    };

    const handleSuccessfulLogin = (data) => {
        if (!!data.isSubscribed) {
            toast.info('Already subscribed.');
            setBackdrop(false);
            handleClose();
            return;
        }

        const userRole = data.userMapping?.entityType?.toLowerCase() || "";
        const userId = data.userMapping?.keycloakUserId || "";
        const orgId = data.userMapping?.organizationId || "";

        localStorage.setItem("token", data.access_token);
        localStorage.setItem("refresh-token", data.refresh_token);
        localStorage.setItem("user-type", userRole);
        localStorage.setItem("user-id", userId);
        localStorage.setItem("org-id", orgId);

        document.cookie = `refresh-token=${data.refresh_token}`;
        document.cookie = `token=${data.access_token}`;
        document.cookie = `user-type=${data.userType}`;

        setUserLoginData({
            userId,
            userRole,
            orgId,
            subscriptionPackage: data.data?.subscriptionPackage || ""
        });
        setBackdrop(false);
    };

    const renderKeycloakSetupModal = () => (
        <CustomModal
            open={showKeycloakSetup}
            handleClose={() => setShowKeycloakSetup(false)}
            title="Complete Account Setup"
            maxWidth="sm"
            fullWidth
        >
            <DialogContent sx={{ p: 0, height: '100%' }}>
                {/* Instructions */}
                <Box sx={{
                    p: 4,
                    backgroundColor: '#e8f4f8',
                    borderBottom: '2px solid #0A48B3'
                }}>
                    <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                        📱 Setup Your Authenticator
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 2 }}>
                        Follow these steps:
                    </Typography>
                    <Typography variant="body1" component="div" sx={{
                        fontSize: '1rem',
                        lineHeight: 1.8,
                        '& br': { content: '""', display: 'block', margin: '8px 0' }
                    }}>
                        <strong>1.</strong> Click "Open Setup Window" to start<br />
                        <strong>2.</strong> Open your authenticator app (Google Authenticator, Authy, etc.)<br />
                        <strong>3.</strong> Scan the QR code in the setup window<br />
                        <strong>4.</strong> Enter the 6-digit code from your app<br />
                        <strong>5.</strong> Click "Save" or "Submit" in the setup form<br />
                        <strong>6.</strong> Close the setup window and click "Setup Complete" below
                    </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{
                    p: 3,
                    backgroundColor: '#f0f8ff',
                    borderTop: '2px solid #0A48B3',
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'stretch'
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={openKeycloakSetup}
                        disabled={keycloakAdapter.isLoading}
                        startIcon={keycloakAdapter.isLoading ? <CircularProgress size={16} color="inherit" /> : <VerifiedUserIcon />}
                        sx={{
                            fontSize: '1rem',
                            py: 1.5
                        }}
                    >
                        {keycloakAdapter.isLoading ? 'Opening...' : '🔧 Open Setup Window'}
                    </Button>

                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={() => checkSetupCompletion(true, usernameRef.current, passwordRef.current, userTypeRef.current)}
                        disabled={setupCheckLoading}
                        startIcon={setupCheckLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        sx={{
                            fontSize: '1rem',
                            py: 1.5
                        }}
                    >
                        {setupCheckLoading ? 'Verifying Setup...' : '✅ Setup Complete - Continue'}
                    </Button>
                </Box>

                {/* Help Text */}
                <Box sx={{
                    p: 2,
                    backgroundColor: '#fff3cd',
                    borderTop: '1px solid #ffeaa7',
                    textAlign: 'center'
                }}>
                    <Typography variant="caption" color="text.secondary">
                        💡 <strong>Tip:</strong> The setup window opens in a separate popup. Please allow popups if prompted. After completing your authenticator setup in the popup, close it and click "Setup Complete" to continue.
                    </Typography>
                </Box>
            </DialogContent>
        </CustomModal>
    );

    // Forgot Password Modal
    const renderForgotPasswordModal = () => {
        return (
            <Dialog
                open={forgotPasswordModalOpen}
                onClose={handleCloseForgotPasswordModal}
                maxWidth="sm"
                fullWidth
            >
                {!forgotPasswordSuccess ? (
                    <>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogContent>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Enter your email address and we'll send you a link to reset your password.
                                </Typography>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={forgotPasswordEmail}
                                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                    placeholder="Enter your registered email"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email sx={{ color: 'action.active' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    error={!!forgotPasswordError}
                                    helperText={forgotPasswordError}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseForgotPasswordModal} disabled={forgotPasswordLoading}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleForgotPasswordSubmit}
                                variant="contained"
                                disabled={forgotPasswordLoading}
                                sx={{ backgroundColor: '#0A48B3' }}
                            >
                                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogContent>
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <CheckCircle sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Success!
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Password reset link sent successfully
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Please check your email inbox
                                </Typography>
                            </Box>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        );
    };

    const renderMfaForm = () => (
        <Formik initialValues={mfaInitialValues} validationSchema={mfaValidationSchema} onSubmit={HandleMfaSubmit}>
            <Form>
                <Grow in timeout={700}>
                    <Box>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" textAlign="center">
                                Enter the code from your authenticator app
                            </Typography>
                        </Box>

                        <div className={styles.inputContainer}>
                            <InputField
                                name="otpCode"
                                type="text"
                                placeholder="Authentication Code *"
                                className={styles.inputField}
                                maxLength={10}
                                adornment={<VerifiedUserIcon />}
                            />
                        </div>

                        <Button
                            variant="contained"
                            type="submit"
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            Verify Code
                        </Button>

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography
                                variant="body2"
                                sx={{ color: '#0A48B3', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => setMfaRequired(false)}
                            >
                                Back to Login
                            </Typography>
                        </Box>
                    </Box>
                </Grow>
            </Form>
        </Formik>
    );


    const renderLoginForm = () => (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={HandleSubmit}>
            {(formik) => (
                <Form>
                    <Grow in timeout={700}>
                        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, margin: 'auto' }}>
                            <TextField
                                fullWidth
                                label="Email"
                                margin="normal"
                                name="username"
                                type="email"
                                value={formik.values.username}
                                onChange={(e) => formik.setFieldValue('username', e.target.value)}
                                onBlur={formik.handleBlur}
                                error={formik.touched.username && Boolean(formik.errors.username)}
                                helperText={formik.touched.username && formik.errors.username}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email />
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Password"
                                margin="normal"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                value={formik.values.password}
                                onChange={(e) => formik.setFieldValue('password', e.target.value)}
                                onBlur={formik.handleBlur}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(prev => !prev)}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <Box display="flex" justifyContent="flex-end" mb={2}>
                                <Link
                                    onClick={handleOpenForgotPasswordModal}
                                    underline="hover"
                                    variant="body2"
                                    sx={{ cursor: "pointer" }}
                                >
                                    Forgot Your Password?
                                </Link>
                            </Box>

                            <Button fullWidth variant="outlined" sx={{ mb: 2 }} onClick={handleClose}>
                                BACK
                            </Button>

                            <Button fullWidth variant="contained" color="primary" type='submit'>
                                {`LOG IN AS ${userType}`}
                            </Button>

                            <Box mt={2} textAlign="center">
                                <Link href={`/${userType}-registration`} underline="hover">
                                    Create new account? Sign-up
                                </Link>
                            </Box>
                        </Paper>
                    </Grow>
                </Form>
            )}
        </Formik>
    );

    return (
        <>
            <DialogContent>
                <Container maxWidth='xs' sx={{ mt: 5 }}>
                    {mfaRequired ? renderMfaForm() : renderLoginForm()}
                    {showKeycloakSetup && renderKeycloakSetupModal()}
                    {forgotPasswordModalOpen && renderForgotPasswordModal()}

                    <Backdrop open={backdrop}>
                        <CircularProgress style={{ color: 'white' }} />
                    </Backdrop>
                </Container>
            </DialogContent>
        </>
    );
};

export default LoginFormUI;
